import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  useTheme,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';

interface AlgorithmsModalProps {
  open: boolean;
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

// Algorithm flowcharts extracted from ALGORITHM_FLOWCHARTS.md
const algorithmDiagrams: Record<string, string> = {
  'Genetic Algorithm': `                    START
                     │
                     ▼
        ┌────────────────────────┐
        │ Initialize Population  │
        │ - Create N chromosomes │
        │ - Random initialization│
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Evaluate Population     │
        │ - Calculate fitness     │
        │ - Find best solution    │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Generation = 1          │
        └────────────────────────┘
                     │
        ┌────────────┴────────────┐
        │                         │
        ▼                         ▼
┌───────────────┐       ┌───────────────┐
│ Calculate RWP │       │ Generation   │
│ for Selection │       │ <= MaxGen?   │
└───────────────┘       └───────────────┘
        │                         │
        │                    ┌────┴────┐
        │                   NO         YES
        │                    │          │
        │                    │          ▼
        │                    │  ┌──────────────┐
        │                    │  │ Return Best  │
        │                    │  └──────────────┘
        ▼                    │
┌───────────────┐            │
│ Crossover     │            │
│ Phase         │            │
│ - Select      │            │
│   parents     │            │
│ - Crossover   │            │
│ - Evaluate    │            │
│ - Replace if  │            │
│   better      │            │
└───────────────┘            │
        │                    │
        ▼                    │
┌───────────────┐            │
│ Mutation      │            │
│ Phase         │            │
│ - Select      │            │
│   chromosome  │            │
│ - Mutate      │            │
│ - Evaluate    │            │
│ - Replace if  │            │
│   better      │            │
└───────────────┘            │
        │                    │
        ▼                    │
┌───────────────┐            │
│ Find Best     │            │
│ Solution      │            │
└───────────────┘            │
        │                    │
        ▼                    │
┌───────────────┐            │
│ Generation++  │            │
└───────────────┘            │
        │                    │
        └────────────────────┘`,

  'Cultural Algorithm': `                    START
                     │
                     ▼
        ┌────────────────────────┐
        │ Initialize Population   │
        │ Evaluate Population     │
        │ eliteCount = 10%        │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Generation = 1          │
        └────────────────────────┘
                     │
        ┌────────────┴────────────┐
        │                         │
        ▼                         ▼
┌───────────────┐       ┌───────────────┐
│ Sort          │       │ Generation    │
│ Population    │       │ <= MaxGen?    │
│ by Fitness    │       └───────────────┘
└───────────────┘              │
        │                 ┌────┴────┐
        ▼                NO         YES
┌───────────────┐         │          │
│ Select Elite  │         │          ▼
│ (top 10%)     │         │  ┌──────────────┐
└───────────────┘         │  │ Return Best  │
        │                 │  └──────────────┘
        ▼                 │
┌───────────────┐         │
│ Elite         │         │
│ Crossover     │         │
│ - Select from │         │
│   elite only  │         │
│ - Crossover   │         │
│ - Replace     │         │
│   worst       │         │
└───────────────┘         │
        │                 │
        ▼                 │
┌───────────────┐         │
│ Mutation      │         │
│ (same as GA)  │         │
└───────────────┘         │
        │                 │
        ▼                 │
┌───────────────┐         │
│ Update Belief │         │
│ Space (Elite) │         │
└───────────────┘         │
        │                 │
        ▼                 │
┌───────────────┐         │
│ Generation++  │         │
└───────────────┘         │
        │                 │
        └─────────────────┘`,

  'SFLA': `                    START
                     │
                     ▼
        ┌────────────────────────┐
        │ Initialize Population  │
        │ (Frogs)                │
        │ Evaluate Population    │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Create Memeplexes       │
        │ - Divide into groups    │
        │ - Round-robin dist.     │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Generation = 1          │
        └────────────────────────┘
                     │
        ┌────────────┴────────────┐
        │                         │
        ▼                         ▼
┌───────────────┐       ┌───────────────┐
│ Find Global   │       │ Generation    │
│ Best          │       │ <= MaxGen?    │
└───────────────┘       └───────────────┘
        │                         │
        │                    ┌────┴────┐
        │                   NO         YES
        │                    │          │
        │                    │          ▼
        │                    │  ┌──────────────┐
        │                    │  │ Return Best  │
        │                    │  └──────────────┘
        ▼                    │
┌───────────────┐            │
│ FOR each       │            │
│ Memeplex:      │            │
└───────────────┘            │
        │                    │
        ▼                    │
┌───────────────┐            │
│ Sort Memeplex │            │
│ (Best→Worst)  │            │
└───────────────┘            │
        │                    │
        ▼                    │
┌───────────────┐            │
│ Update Worst   │            │
│ Frog           │            │
│ - Try local    │            │
│   best         │            │
│ - Try global   │            │
│   best         │            │
│ - Random if    │            │
│   failed       │            │
└───────────────┘            │
        │                    │
        ▼                    │
┌───────────────┐            │
│ Shuffle        │            │
│ Memeplexes     │            │
│ (Redistribute) │            │
└───────────────┘            │
        │                    │
        ▼                    │
┌───────────────┐            │
│ Recreate      │            │
│ Memeplexes    │            │
└───────────────┘            │
        │                    │
        ▼                    │
┌───────────────┐            │
│ Generation++  │            │
└───────────────┘            │
        │                    │
        └────────────────────┘`,

  'SFLAGA': `                    START
                     │
                     ▼
        ┌────────────────────────┐
        │ Initialize Population  │
        │ Create Memeplexes       │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Generation = 1          │
        └────────────────────────┘
                     │
        ┌────────────┴────────────┐
        │                         │
        ▼                         ▼
┌───────────────┐       ┌───────────────┐
│ Find Global   │       │ Generation    │
│ Best          │       │ <= MaxGen?    │
└───────────────┘       └───────────────┘
        │                         │
        │                    ┌────┴────┐
        │                   NO         YES
        │                    │          │
        │                    │          ▼
        │                    │  ┌──────────────┐
        │                    │  │ Return Best  │
        │                    │  └──────────────┘
        ▼                    │
┌───────────────┐            │
│ FOR each       │            │
│ Memeplex:      │            │
└───────────────┘            │
        │                    │
        ▼                    │
┌───────────────┐            │
│ Calculate RWP │            │
│ for Memeplex  │            │
└───────────────┘            │
        │                    │
        ▼                    │
┌───────────────┐            │
│ GA Crossover  │            │
│ - 33% use     │            │
│   global best │            │
│ - Select from │            │
│   memeplex    │            │
│ - Replace     │            │
│   worst       │            │
└───────────────┘            │
        │                    │
        ▼                    │
┌───────────────┐            │
│ GA Mutation   │            │
│ (within       │            │
│  memeplex)    │            │
└───────────────┘            │
        │                    │
        ▼                    │
┌───────────────┐            │
│ Shuffle       │            │
│ Memeplexes    │            │
└───────────────┘            │
        │                    │
        ▼                    │
┌───────────────┐            │
│ Generation++  │            │
└───────────────┘            │
        │                    │
        └────────────────────┘`,

  'SFLACA': `                    START
                     │
                     ▼
        ┌────────────────────────┐
        │ Initialize Population  │
        │ Create Memeplexes       │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Generation = 1          │
        └────────────────────────┘
                     │
        ┌────────────┴────────────┐
        │                         │
        ▼                         ▼
┌───────────────┐       ┌───────────────┐
│ Find Global   │       │ Generation    │
│ Best          │       │ <= MaxGen?    │
└───────────────┘       └───────────────┘
        │                         │
        │                    ┌────┴────┐
        │                   NO         YES
        │                    │          │
        │                    │          ▼
        │                    │  ┌──────────────┐
        │                    │  │ Return Best  │
        │                    │  └──────────────┘
        ▼                    │
┌───────────────┐            │
│ FOR each       │            │
│ Memeplex:      │            │
└───────────────┘            │
        │                    │
        ▼                    │
┌───────────────┐            │
│ SFLA Local     │            │
│ Search on      │            │
│ Worst Frog     │            │
└───────────────┘            │
        │                    │
        ▼                    │
┌───────────────┐            │
│ CA Elite-Based │            │
│ Crossover      │            │
│ - Select elite │            │
│   parents      │            │
│ - Belief space │            │
│   influence    │            │
│ - Replace      │            │
│   worst        │            │
└───────────────┘            │
        │                    │
        ▼                    │
┌───────────────┐            │
│ Mutation      │            │
│ (within       │            │
│  memeplex)    │            │
└───────────────┘            │
        │                    │
        ▼                    │
┌───────────────┐            │
│ Shuffle       │            │
│ Memeplexes    │            │
└───────────────┘            │
        │                    │
        ▼                    │
┌───────────────┐            │
│ Generation++  │            │
└───────────────┘            │
        │                    │
        └────────────────────┘`,

  'PSO': `                    START
                     │
                     ▼
        ┌────────────────────────┐
        │ Initialize Swarm        │
        │ - Random positions      │
        │ - Zero velocities      │
        │ - pBest = position     │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Evaluate Swarm          │
        │ Find Global Best        │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Generation = 1        │
        └────────────────────────┘
                     │
        ┌────────────┴────────────┐
        │                         │
        ▼                         ▼
┌───────────────┐       ┌───────────────┐
│ Calculate     │       │ Generation    │
│ Inertia       │       │ <= MaxGen?    │
│ Weight (w)    │       └───────────────┘
│ w = 0.9→0.4   │              │
└───────────────┘         ┌────┴────┐
        │                NO         YES
        │                 │          │
        │                 │          ▼
        ▼                 │  ┌──────────────┐
┌───────────────┐         │  │ Return Best  │
│ FOR each       │         │  └──────────────┘
│ Particle:      │         │
└───────────────┘         │
        │                 │
        ▼                 │
┌───────────────┐         │
│ Update        │         │
│ Velocity      │         │
│ v = w×v +     │         │
│   c1×r1×      │         │
│   (pBest-x) + │         │
│   c2×r2×      │         │
│   (gBest-x)   │         │
└───────────────┘         │
        │                 │
        ▼                 │
┌───────────────┐         │
│ Update        │         │
│ Position      │         │
│ Based on      │         │
│ Velocity      │         │
│ Probability   │         │
└───────────────┘         │
        │                 │
        ▼                 │
┌───────────────┐         │
│ Evaluate      │         │
│ Particle      │         │
└───────────────┘         │
        │                 │
        ▼                 │
┌───────────────┐         │
│ Update        │         │
│ Personal Best │         │
└───────────────┘         │
        │                 │
        ▼                 │
┌───────────────┐         │
│ Update        │         │
│ Global Best   │         │
└───────────────┘         │
        │                 │
        ▼                 │
┌───────────────┐         │
│ Generation++  │         │
└───────────────┘         │
        │                 │
        └─────────────────┘`,

  'GAPSO': `                    START
                     │
                     ▼
        ┌────────────────────────┐
        │ Initialize Swarm        │
        │ Evaluate Swarm          │
        │ Find Global Best        │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Generation = 1           │
        └────────────────────────┘
                     │
        ┌────────────┴────────────┐
        │                         │
        ▼                         ▼
┌───────────────┐       ┌───────────────┐
│ Calculate     │       │ Generation    │
│ Inertia       │       │ <= MaxGen?    │
│ Weight (w)    │       └───────────────┘
└───────────────┘              │
        │                 ┌────┴────┐
        │                NO         YES
        │                 │          │
        │                 │          ▼
        ▼                 │  ┌──────────────┐
┌───────────────┐        │  │ Return Best  │
│ PSO Update      │        │  └──────────────┘
│ Phase           │        │
│ - Update        │        │
│   velocity      │        │
│ - Update        │        │
│   position      │        │
│ - Evaluate      │        │
│ - Update pBest  │        │
└───────────────┘        │
        │                 │
        ▼                 │
┌───────────────┐        │
│ GA Crossover   │        │
│ Phase          │        │
│ - Convert to   │        │
│   chromosomes  │        │
│ - Calculate    │        │
│   RWP          │        │
│ - Crossover    │        │
│ - Create new   │        │
│   particles    │        │
│ - Replace if   │        │
│   better       │        │
└───────────────┘        │
        │                 │
        ▼                 │
┌───────────────┐        │
│ Update        │        │
│ Global Best   │        │
└───────────────┘        │
        │                 │
        ▼                 │
┌───────────────┐        │
│ Generation++  │        │
└───────────────┘        │
        │                 │
        └─────────────────┘`,

  'Kurd SFLA': `                    START
                     │
                     ▼
        ┌────────────────────────┐
        │ Initialize Population  │
        │ (Frogs)                │
        │ Evaluate Population    │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Create Memeplexes       │
        │ - Divide into groups    │
        │ - Round-robin dist.     │
        └────────────────────────┘
                     │
                     ▼
        ┌────────────────────────┐
        │ Generation = 1          │
        └────────────────────────┘
                     │
        ┌────────────┴────────────┐
        │                         │
        ▼                         ▼
┌───────────────┐       ┌───────────────┐
│ Find Global   │       │ Generation    │
│ Best          │       │ <= MaxGen?    │
└───────────────┘       └───────────────┘
        │                         │
        │                    ┌────┴────┐
        │                   NO         YES
        │                    │          │
        │                    │          ▼
        │                    │  ┌──────────────┐
        │                    │  │ Return Best  │
        │                    │  └──────────────┘
        ▼                    │
┌───────────────┐            │
│ FOR each       │            │
│ Memeplex:      │            │
└───────────────┘            │
        │                    │
        ▼                    │
┌───────────────┐            │
│ Sort Memeplex │            │
│ (Best→Worst)  │            │
└───────────────┘            │
        │                    │
        ▼                    │
┌───────────────┐            │
│ Update Worst   │            │
│ Frog (Kurd     │            │
│ variant)       │            │
│ - Try local    │            │
│   best         │            │
│ - Try global   │            │
│   best         │            │
│ - Random if    │            │
│   failed       │            │
└───────────────┘            │
        │                    │
        ▼                    │
┌───────────────┐            │
│ Shuffle        │            │
│ Memeplexes     │            │
│ (Redistribute) │            │
└───────────────┘            │
        │                    │
        ▼                    │
┌───────────────┐            │
│ Recreate      │            │
│ Memeplexes    │            │
└───────────────┘            │
        │                    │
        ▼                    │
┌───────────────┐            │
│ Generation++  │            │
└───────────────┘            │
        │                    │
        └────────────────────┘`,
};

const AlgorithmsModal: React.FC<AlgorithmsModalProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const [value, setValue] = useState(0);

  const algorithmFiles = [
    { name: 'Genetic Algorithm', key: 'Genetic Algorithm' },
    { name: 'Cultural Algorithm', key: 'Cultural Algorithm' },
    { name: 'SFLA', key: 'SFLA' },
    { name: 'SFLAGA', key: 'SFLAGA' },
    { name: 'SFLACA', key: 'SFLACA' },
    { name: 'PSO', key: 'PSO' },
    { name: 'GAPSO', key: 'GAPSO' },
    { name: 'Kurd SFLA', key: 'Kurd SFLA' },
  ];

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CodeIcon sx={{ fontSize: 28 }} />
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            Algorithm Flowcharts
          </Typography>
        </Box>
        <Button
          onClick={onClose}
          sx={{
            color: 'white',
            minWidth: 'auto',
            '&:hover': {
              backgroundColor: alpha('#ffffff', 0.1),
            },
          }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={value}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minHeight: 48,
              },
            }}
          >
            {algorithmFiles.map((alg, index) => (
              <Tab key={index} label={alg.name} />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {algorithmFiles.map((alg, index) => (
            <TabPanel key={index} value={value} index={index}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  backgroundColor: alpha(theme.palette.background.default, 0.5),
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                  {alg.name}
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    margin: 0,
                    padding: 3,
                    backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#fafafa',
                    borderRadius: 1,
                    overflow: 'auto',
                    fontSize: '0.8125rem',
                    fontFamily: '"Courier New", Courier, "Lucida Console", Monaco, monospace',
                    lineHeight: 1.5,
                    border: `1px solid ${theme.palette.divider}`,
                    whiteSpace: 'pre',
                    color: theme.palette.text.primary,
                  }}
                >
                  {algorithmDiagrams[alg.key] || `Diagram for ${alg.name} not available.`}
                </Box>
              </Paper>
            </TabPanel>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlgorithmsModal;
